import Check from '../Check';
import Report from '../Report';
import { MessageType } from '../Message';
import type { IMethod } from '../../../stores/model/metamodel/method';

/**
 * Check the consistency of a specialised method.
 *
 * Checks that:
 * - the method has a viaInheritance
 * - the method has a viaMethod
 * - the types of the method and its viaMethod are consistent
 * - the provenance is set to SPECIALISED
 */
class SpecialisedMethodConsistencyCheck extends Check<IMethod> {
  static description = 'Check consistency of specialised method';

  check(): Report {
    this.report = new Report();

    // Check if there is a via method
    if (this.element.viaMethod === null) {
      // Oops, there is no via method
      this.report.addMessage(
        MessageType.ERROR,
        `Specialised method (${this.element.asString()}) has no originating method.`,
        this,
        this.element
      );
    } else {
      // There is a via method
      this.report.addMessage(
        MessageType.INFORMATION,
        `Specialised method (${this.element.asString()}) has originating method.`,
        this,
        this.element
      );

      // Now check whether the type is consistent with the type of the via
      // method
      const viaType = this.element.viaMethod.type;
      const type = this.element.type;
      if (type !== viaType) {
        this.report.addMessage(
          MessageType.ERROR,
          "The specialised method type is not set to the type of it's originating method.",
          this,
          this.element
        );
      } else {
        this.report.addMessage(
          MessageType.INFORMATION,
          'The specialised method has a valid type.',
          this,
          this.element
        );
      }
    }

    // Finally, check whether the provenance is set to INHERITED.
    if (this.element.provenance !== 'SPECIALISED') {
      this.report.addMessage(
        MessageType.ERROR,
        `Specialised method (${this.element.asString()}) does not have its provenance set to SPECIALISED.`,
        this,
        this.element
      );
    } else {
      this.report.addMessage(
        MessageType.INFORMATION,
        `Specialised method (${this.element.asString()}) provenance is set correctly.`,
        this,
        this.element
      );
    }

    return this.report;
  }
}

export default SpecialisedMethodConsistencyCheck;
